import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { collection, addDoc } from 'firebase/firestore';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { theme as muiTheme } from '../styles/MUITheme';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { db } from '../firebase';
import { getAllProducts } from '../firebase/products';
import JobSticker from '../components/JobSticker';
import EditProductModal from '../components/EditProductModal';
import AddProduct from '../components/AddProduct';
import OrderingSidebar from '../components/ordering/OrderingSidebar';
import PurchaseOrderTable from '../components/ordering/PurchaseOrderTable';
import { subscribeReadyToOrderData } from '../utils/orderingData';
import { markLinesAsOrdered } from '../utils/orderingMarkOrdered';
import { generateOrderingSummaryPdf } from '../utils/orderingPdf';
import { printLabelBase64 } from '../utils/printServer';
import { fetchStickerPrintPayload } from '../utils/fetchStickerPrintPayload';
import {
  buildPurchaseOrderGroups,
  extractStyleNo,
  lineTotalPieces,
  lineCogsTotal,
} from '../utils/sizeGrids';
import { formatCurrency } from '../utils/formatters';
import { useFacility } from '../context/FacilityContext';

const ACCENT = 'var(--al-primary)';
const PAGE_BG = 'var(--al-bg)';
const CARD_BG = 'var(--al-surface)';

const Page = styled.div`
  width: 100%;
  padding: 80px 24px 24px;
  background: ${PAGE_BG};
  min-height: 100vh;
  color: #fff;
  font-family: 'Montserrat', sans-serif;
  box-sizing: border-box;
`;

const PageInner = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${CARD_BG};
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  border-radius: 8px;
  padding: 16px 20px;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${(props) => (props.$warn ? '#ffb74d' : ACCENT)};
  line-height: 1.2;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const MainPanel = styled.div`
  background: ${CARD_BG};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px 24px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

const PanelTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding-top: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: ${CARD_BG};
`;

const alertSx = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1100,
};

function OrderingDashboard() {
  const { facilityId } = useFacility();
  const [orders, setOrders] = useState([]);
  const [linesByOrder, setLinesByOrder] = useState({});
  const [selectedLines, setSelectedLines] = useState({});
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [addProductStyleNo, setAddProductStyleNo] = useState(null);

  const [isPrinting, setIsPrinting] = useState(false);
  const [printQueue, setPrintQueue] = useState([]);
  const [currentPrintIndex, setCurrentPrintIndex] = useState(0);
  const [printProgress, setPrintProgress] = useState('');
  const printQueueRef = useRef([]);

  const selectedLineIds = useMemo(
    () => Object.entries(selectedLines).filter(([, checked]) => checked).map(([id]) => id),
    [selectedLines]
  );

  const selectedCount = selectedLineIds.length;

  const allLineIds = useMemo(
    () => Object.values(linesByOrder).flat().map((line) => line.id),
    [linesByOrder]
  );

  const stats = useMemo(() => {
    const allLines = Object.values(linesByOrder).flat();
    const totalPieces = allLines.reduce((sum, line) => sum + lineTotalPieces(line), 0);
    const missingUrlCount = allLines.filter((line) => {
      const styleNo = extractStyleNo(line.garmentId);
      const product = products.find((p) => p.styleNo === styleNo);
      return !product?.supplierUrl;
    }).length;

    const selectedPieces = allLines
      .filter((line) => selectedLines[line.id])
      .reduce((sum, line) => sum + lineTotalPieces(line), 0);

    const totalCogs = allLines.reduce((sum, line) => sum + lineCogsTotal(line, products), 0);
    const selectedCogs = allLines
      .filter((line) => selectedLines[line.id])
      .reduce((sum, line) => sum + lineCogsTotal(line, products), 0);

    return {
      orderCount: orders.length,
      lineCount: allLines.length,
      totalPieces,
      totalCogs,
      missingUrlCount,
      selectedPieces,
      selectedCogs,
    };
  }, [orders, linesByOrder, products, selectedLines]);

  const purchaseOrderGroups = useMemo(
    () => buildPurchaseOrderGroups(selectedLineIds, linesByOrder, products),
    [selectedLineIds, linesByOrder, products]
  );

  const showSuccess = useCallback((message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 4000);
  }, []);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000);
  }, []);

  const refreshProducts = useCallback(async () => {
    const data = await getAllProducts();
    setProducts(data);
  }, []);

  useEffect(() => {
    let active = true;
    getAllProducts()
      .then((data) => {
        if (active) setProducts(data);
      })
      .catch((err) => console.error('Error loading products:', err));

    setLoading(true);
    const unsubscribe = subscribeReadyToOrderData(db, {
      facilityId,
      onData: ({ orders: nextOrders, linesByOrder: nextLinesByOrder }) => {
        if (!active) return;
        setOrders(nextOrders);
        setLinesByOrder(nextLinesByOrder);
        setLoading(false);

        setSelectedLines((prev) => {
          const validIds = new Set(
            Object.values(nextLinesByOrder)
              .flat()
              .map((line) => line.id)
          );
          const next = {};
          Object.entries(prev).forEach(([id, checked]) => {
            if (checked && validIds.has(id)) next[id] = true;
          });
          return next;
        });
      },
      onError: (err) => {
        console.error('Error subscribing to ordering data:', err);
        if (active) {
          setLoading(false);
          showError('Failed to load ordering data');
        }
      },
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [showError, facilityId]);

  const isOrderChecked = useCallback(
    (orderId) => {
      const lines = linesByOrder[orderId] || [];
      return lines.length > 0 && lines.every((line) => selectedLines[line.id]);
    },
    [linesByOrder, selectedLines]
  );

  const isOrderIndeterminate = useCallback(
    (orderId) => {
      const lines = linesByOrder[orderId] || [];
      const checkedCount = lines.filter((line) => selectedLines[line.id]).length;
      return checkedCount > 0 && checkedCount < lines.length;
    },
    [linesByOrder, selectedLines]
  );

  const handleOrderCheck = useCallback(
    (orderId, checked) => {
      const lines = linesByOrder[orderId] || [];
      setSelectedLines((prev) => {
        const updated = { ...prev };
        lines.forEach((line) => {
          updated[line.id] = checked;
        });
        return updated;
      });
    },
    [linesByOrder]
  );

  const handleLineCheck = useCallback((lineId, checked) => {
    setSelectedLines((prev) => ({ ...prev, [lineId]: checked }));
  }, []);

  const handleSelectAll = useCallback(() => {
    const next = {};
    allLineIds.forEach((id) => {
      next[id] = true;
    });
    setSelectedLines(next);
  }, [allLineIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedLines({});
  }, []);

  const handleFixLink = useCallback((row) => {
    if (row.product) {
      setEditingProduct(row.product);
      return;
    }
    setAddProductStyleNo(row.styleNo);
  }, []);

  const handleProductSaved = useCallback(async () => {
    await refreshProducts();
    setEditingProduct(null);
    showSuccess('Supplier link updated');
  }, [refreshProducts, showSuccess]);

  const handleAddProduct = useCallback(
    async (productData) => {
      try {
        await addDoc(collection(db, 'products'), {
          ...productData,
          cogs: parseFloat(productData.cogs) || 0,
          createdAt: new Date(),
        });
        await refreshProducts();
        setAddProductStyleNo(null);
        showSuccess('Product added — supplier link saved');
      } catch (error) {
        console.error('Error adding product:', error);
        showError('Failed to add product');
      }
    },
    [refreshProducts, showSuccess, showError]
  );

  const getSelectedLinesWithOrders = useCallback(() => {
    const result = [];
    for (const orderId of Object.keys(linesByOrder)) {
      const order = orders.find((o) => o.id === orderId);
      for (const line of linesByOrder[orderId] || []) {
        if (selectedLines[line.id]) {
          result.push({ line, order });
        }
      }
    }
    return result;
  }, [selectedLines, linesByOrder, orders]);

  const getFreshSelectedLinesWithOrders = useCallback(async () => {
    const selected = getSelectedLinesWithOrders();
    return Promise.all(
      selected.map(async ({ line, order }) => {
        try {
          const fresh = await fetchStickerPrintPayload(db, line.id, line.orderId || order?.id);
          return {
            line: fresh.line,
            order: fresh.order || order,
          };
        } catch (error) {
          console.error(`Error refreshing line ${line.id} for sticker:`, error);
          return { line, order };
        }
      })
    );
  }, [getSelectedLinesWithOrders]);

  const buildLineStatusMap = useCallback(() => {
    const map = {};
    Object.values(linesByOrder)
      .flat()
      .forEach((line) => {
        map[line.id] = line.status;
      });
    return map;
  }, [linesByOrder]);

  const handleMarkAsOrdered = useCallback(
    async ({ printDocs = false } = {}) => {
      if (selectedLineIds.length === 0) return;

      setSubmitting(true);
      setConfirmationOpen(false);

      try {
        const linesWithOrders = printDocs
          ? await getFreshSelectedLinesWithOrders()
          : getSelectedLinesWithOrders();

        if (printDocs) {
          printQueueRef.current = linesWithOrders;
          setIsPrinting(true);
          setPrintQueue(linesWithOrders);
          setCurrentPrintIndex(0);
          setPrintProgress(`Printing sticker 1 of ${linesWithOrders.length}...`);
        }

        await markLinesAsOrdered(db, {
          selectedLineIds,
          linesByOrder,
          lineStatusById: buildLineStatusMap(),
        });

        setSelectedLines({});

        if (!printDocs) {
          showSuccess(
            `Marked ${selectedLineIds.length} line${selectedLineIds.length !== 1 ? 's' : ''} as ordered`
          );
        }
      } catch (error) {
        console.error('Error marking as ordered:', error);
        showError('Failed to update lines/orders');
        setIsPrinting(false);
        setPrintQueue([]);
        setPrintProgress('');
      } finally {
        setSubmitting(false);
      }
    },
    [
      selectedLineIds,
      getSelectedLinesWithOrders,
      getFreshSelectedLinesWithOrders,
      linesByOrder,
      buildLineStatusMap,
      showSuccess,
      showError,
    ]
  );

  const handleStickerPdfGenerated = useCallback(
    async (pdfData) => {
      if (!pdfData || !isPrinting) return;

      try {
        await printLabelBase64(pdfData);
      } catch (error) {
        console.error('Print error:', error);
      }

      const queue = printQueueRef.current;
      if (currentPrintIndex + 1 < queue.length) {
        const nextIndex = currentPrintIndex + 1;
        setCurrentPrintIndex(nextIndex);
        setPrintProgress(`Printing sticker ${nextIndex + 1} of ${queue.length}...`);
      } else {
        setPrintProgress('All stickers printed! Generating summary...');
        try {
          await generateOrderingSummaryPdf(queue);
          showSuccess(`Marked ${queue.length} lines as ordered and printed docs`);
        } catch (error) {
          console.error('PDF error:', error);
          showError('Stickers printed but summary PDF failed');
        }
        setIsPrinting(false);
        setPrintQueue([]);
        printQueueRef.current = [];
        setCurrentPrintIndex(0);
        setPrintProgress('');
      }
    },
    [isPrinting, currentPrintIndex, showSuccess, showError]
  );

  return (
    <ThemeProvider theme={muiTheme}>
    <Page>
      <PageInner>
        <Typography
          variant="h4"
          sx={{ color: ACCENT, fontWeight: 600, mb: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          Ordering Dashboard
        </Typography>

        <StatsBar>
          <StatCard>
            <StatLabel>Orders ready</StatLabel>
            <StatValue>{stats.orderCount}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Lines to order</StatLabel>
            <StatValue>{stats.lineCount}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Total pieces</StatLabel>
            <StatValue>{stats.totalPieces.toLocaleString()}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Total COGS</StatLabel>
            <StatValue>{formatCurrency(stats.totalCogs)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Missing supplier URL</StatLabel>
            <StatValue $warn={stats.missingUrlCount > 0}>{stats.missingUrlCount}</StatValue>
          </StatCard>
        </StatsBar>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300} gap={2}>
            <CircularProgress sx={{ color: ACCENT }} />
            <Typography color="rgba(255,255,255,0.7)">Loading orders...</Typography>
          </Box>
        ) : (
          <DashboardGrid>
            <OrderingSidebar
              orders={orders}
              linesByOrder={linesByOrder}
              products={products}
              selectedLines={selectedLines}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onOrderCheck={handleOrderCheck}
              onLineCheck={handleLineCheck}
              onSelectAll={handleSelectAll}
              onClearSelection={handleClearSelection}
              isOrderChecked={isOrderChecked}
              isOrderIndeterminate={isOrderIndeterminate}
            />

            <MainPanel>
              <PanelTitle>
                Purchase order
                {selectedCount > 0 && ` — ${selectedCount} line${selectedCount !== 1 ? 's' : ''} · ${stats.selectedPieces.toLocaleString()} pcs`}
              </PanelTitle>

              {stats.missingUrlCount > 0 && selectedCount > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 183, 77, 0.1)',
                    border: '1px solid rgba(255, 183, 77, 0.3)',
                  }}
                >
                  <WarningAmberIcon sx={{ color: '#ffb74d', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                    Some selected styles are missing supplier URLs. Use <strong>Fix Link!</strong> on the row to add one without leaving this page.
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PurchaseOrderTable groups={purchaseOrderGroups} onFixLink={handleFixLink} />
              </Box>

              {selectedCount > 0 && (
                <ActionBar>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {selectedCount} selected · {stats.selectedPieces.toLocaleString()} pieces
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={handleClearSelection}
                      disabled={submitting || isPrinting}
                      sx={{
                        color: 'rgba(255,255,255,0.7)',
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': { borderColor: ACCENT, color: ACCENT },
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      disabled={submitting || isPrinting}
                      onClick={() => setConfirmationOpen(true)}
                      sx={{
                        backgroundColor: ACCENT,
                        color: 'var(--al-bg)',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#7FFF7F' },
                      }}
                    >
                      Mark as Ordered
                    </Button>
                  </Box>
                </ActionBar>
              )}
            </MainPanel>
          </DashboardGrid>
        )}

        <Dialog
          open={confirmationOpen}
          onClose={() => !submitting && setConfirmationOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { backgroundColor: CARD_BG, color: '#fff' } }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            Confirm order status update
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>
              Mark {selectedCount} line{selectedCount !== 1 ? 's' : ''} as ordered?
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>
              Selected items will move to the receiving dashboard.
            </Typography>
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(var(--al-primary-rgb), 0.1)', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: ACCENT, fontWeight: 600 }}>
                Print docs will:
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', ml: 2, mt: 0.5 }}>
                • Print {selectedCount} sticker{selectedCount !== 1 ? 's' : ''} (one per line)
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', ml: 2 }}>
                • Download a summary PDF checklist
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2, gap: 1 }}>
            <Button onClick={() => setConfirmationOpen(false)} disabled={submitting} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleMarkAsOrdered({ printDocs: false })}
              disabled={submitting}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: '#fff',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              Mark only
            </Button>
            <Button
              onClick={() => handleMarkAsOrdered({ printDocs: true })}
              disabled={submitting}
              startIcon={<PrintIcon />}
              sx={{
                backgroundColor: ACCENT,
                color: 'var(--al-bg)',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#7FFF7F' },
              }}
            >
              Mark + Print docs
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isPrinting || printProgress !== ''}
          PaperProps={{ sx: { backgroundColor: CARD_BG, color: '#fff', minWidth: 350 } }}
        >
          <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalPrintshopIcon sx={{ color: ACCENT }} />
            Printing documents
          </DialogTitle>
          <DialogContent sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress sx={{ color: ACCENT, mb: 2 }} />
            <Typography>{printProgress || 'Processing...'}</Typography>
            {isPrinting && printQueue.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.6)' }}>
                {currentPrintIndex + 1} of {printQueue.length} stickers
              </Typography>
            )}
          </DialogContent>
        </Dialog>

        {isPrinting && printQueue.length > 0 && printQueue[currentPrintIndex] && (
          <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <JobSticker
              line={printQueue[currentPrintIndex].line}
              order={printQueue[currentPrintIndex].order}
              onPdfGenerated={handleStickerPdfGenerated}
            />
          </div>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={alertSx}
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setSuccessMessage('')}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{ ...alertSx, bottom: successMessage ? 88 : 24 }}
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setErrorMessage('')}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {errorMessage}
          </Alert>
        )}

        {editingProduct && (
          <EditProductModal
            product={editingProduct}
            focusSupplierUrl
            onClose={() => setEditingProduct(null)}
            onSaved={handleProductSaved}
          />
        )}

        <AddProduct
          key={addProductStyleNo || 'closed'}
          isOpen={!!addProductStyleNo}
          onClose={() => setAddProductStyleNo(null)}
          onAdd={handleAddProduct}
          initialData={{ styleNo: addProductStyleNo || '' }}
        />
      </PageInner>
    </Page>
    </ThemeProvider>
  );
}

export default OrderingDashboard;
